import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Typography, Card, Space, Button, Form, Input, Select, Upload, message, List, Divider, Empty, Slider, InputNumber, Row, Col, Avatar, Tag, Checkbox } from 'antd';
import { InboxOutlined, FilterOutlined, DollarOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined, HomeOutlined, ManOutlined, WomanOutlined, TeamOutlined, HeartOutlined } from '@ant-design/icons';
import './NeighborhoodLens.css';
import api from '../../../utils/api';

function NeighborhoodLens() {
  const { Title, Paragraph, Text } = Typography;
  const [datasets, setDatasets] = useState([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState(null);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [addressForm] = Form.useForm();
  const [addrFileList, setAddrFileList] = useState([]);
  const [filtering, setFiltering] = useState(false);
  const [results, setResults] = useState([]);
  const [radius, setRadius] = useState(1.0); // miles
  const [selectedIds, setSelectedIds] = useState([]);
  const [page, setPage] = useState(1);
  const resultsCardRef = useRef(null);
  const [searchQuery, setSearchQuery] = useState('');

  const durationOptions = [
    { label: 'Last 7 days', value: '7d' },
    { label: 'Last 14 days', value: '14d' },
    { label: 'Last 30 days', value: '30d' },
    { label: 'Last 90 days', value: '90d' }
  ];

  const loadDatasets = useCallback(async () => {
    setLoadingDatasets(true);
    try {
      const data = await api.get('/audience-datasets');
      const items = Array.isArray(data?.items) ? data.items : Array.isArray(data) ? data : [];
      setDatasets(items);
    } catch (e) {
      message.error(e.message || 'Failed to load datasets');
    } finally {
      setLoadingDatasets(false);
    }
  }, []);

  const filteredResults = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return results;
    return results.filter(r => {
      const hay = [r.name, r.email, r.address].filter(Boolean).join(' ').toLowerCase();
      return hay.includes(q);
    });
  }, [results, searchQuery]);

  const quickSetRadius = (val) => setRadius(val);

  const exportResults = (data) => {
    const rowsToExport = data ?? results;
    if (!rowsToExport || rowsToExport.length === 0) {
      message.warning('No results to export');
      return;
    }
    const columns = [
      'initials','name','age','income','marital','home','gender','children','email','phone','address','distance','nearest'
    ];
    const header = columns.join(',');
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = rowsToExport.map(r => columns.map(c => escape(r[c] ?? '')).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `neighborhood-lens-results-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadDatasets();
  }, [loadDatasets]);

  const uploadAddrProps = useMemo(() => ({
    multiple: false,
    accept: '.csv,.txt',
    beforeUpload: (file) => {
      setAddrFileList([file]);
      return false;
    },
    onRemove: () => setAddrFileList([]),
    fileList: addrFileList
  }), [addrFileList]);

  const handleFilter = async () => {
    try {
      const values = await addressForm.validateFields();
      const { address, pastedAddresses } = values;
      if (!address && addrFileList.length === 0 && !pastedAddresses) {
        message.error('Enter an address, upload a CSV, or paste addresses to continue');
        return;
      }
      setFiltering(true);
      // Gather addresses from form
      const addresses = [];
      if (address) addresses.push(address);
      if (pastedAddresses) addresses.push(...pastedAddresses.split(/\r?\n/).map(s => s.trim()).filter(Boolean));
      if (addrFileList.length) {
        try {
          const file = addrFileList[0];
          const text = await file.originFileObj.text();
          const lines = text.split(/\r?\n/).map(s => s.trim()).filter(Boolean);
          addresses.push(...lines);
        } catch (err) {
          message.warning('Could not read uploaded file; continuing with typed addresses only');
        }
      }

      // Call backend analyze endpoint (scaffold geocoding + recent audience_lab scoped by dataset)
      const resp = await api.post('/neighborhood-lens/analyze', {
        addresses,
        radiusMi: radius,
        datasetId: selectedDatasetId || undefined
      });
      const raw = Array.isArray(resp?.items) ? resp.items : [];
      // Normalize to objects with at least an id and displayable fields
      const items = raw.map((r, idx) => {
        if (r && typeof r === 'object') {
          const nameJoined = [r.first_name, r.last_name].filter(Boolean).join(' ');
          const personalAddressJoined = [r.personal_address, r.personal_city, r.personal_state, r.personal_zip].filter(Boolean).join(', ');
          const ageRaw = r.age ?? r.skiptrace_exact_age ?? r.age_range ?? r.AGE_RANGE ?? r.AGE;
          const ageStr = (ageRaw === null || ageRaw === undefined) ? '' : String(ageRaw);
          let age = ageStr && ageStr.toLowerCase() !== 'null' ? ageStr : 'N/A';
          if (age === 'N/A') {
            try {
              const key = Object.keys(r).find(k => /age/i.test(k) && r[k] !== null && r[k] !== undefined && String(r[k]).trim().toLowerCase() !== 'null' && String(r[k]).trim() !== '');
              if (key) age = String(r[key]);
            } catch (_) {}
          }
          return {
            id: r.id ?? r.uuid ?? idx + 1,
            name: (r.name ?? r.full_name ?? nameJoined) ?? '',
            email: (r.email ?? r.business_email ?? (Array.isArray(r.personal_emails) ? r.personal_emails[0] : r.personal_emails)) ?? '',
            address: (r.address ?? (personalAddressJoined ? personalAddressJoined : undefined) ?? r.company_address) ?? '',
            age,
            income: r.income ?? r.income_range ?? '',
            marital: r.marital ?? (r.married === true ? 'Married' : r.married === false ? 'Single' : ''),
            home: r.home ?? (r.homeowner === true ? 'Owner' : r.homeowner === false ? 'Renter' : ''),
            gender: r.gender ?? '',
            children: r.children ?? '',
            distance: r.distance ?? r.distance_miles ?? '',
            nearest: r.nearest ?? '',
            initials: r.initials ?? (() => {
              const nm = (r.name ?? r.full_name ?? '').toString().trim();
              if (!nm) return '';
              const parts = nm.split(/\s+/).filter(Boolean);
              return ((parts[0]?.[0] || '') + (parts[1]?.[0] || '')).toUpperCase();
            })(),
          };
        }
        // Primitive fallback
        return { id: idx + 1, name: String(r), email: '', address: '' };
      });
      setResults(items);
      setSelectedIds([]);
      setPage(1);
      if (items.length > 0) {
        message.success(`Results loaded (${items.length})`);
        // Scroll results into view
        try { resultsCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (_) {}
      } else {
        message.warning('No matches found. Try a different address fragment (e.g., city, ZIP) or clear the dataset to search all.');
        try { resultsCardRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }); } catch (_) {}
      }
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Filter failed');
    } finally {
      setFiltering(false);
    }
  };

  return (
    <div className="neighborhood-lens-page">
      <Title level={3}>NeighborhoodLens</Title>

      <Card title="Admin Datasets" style={{ marginBottom: 16 }}>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Datasets uploaded by admin (name and row count).
        </Paragraph>
        <div style={{ marginBottom: 12 }}>
          <Space>
            <Text strong>Select dataset for filtering:</Text>
            <Select
              allowClear
              value={selectedDatasetId ?? null}
              onChange={(val) => setSelectedDatasetId(val ?? null)}
              style={{ minWidth: 320 }}
              options={datasets.map(d => ({ label: `${d.name || `Dataset #${d.id}`} (${d.row_count ?? 0} rows)`, value: d.id }))}
              placeholder="All datasets (clear to search across all)"
            />
          </Space>
        </div>
        <List
          loading={loadingDatasets}
          locale={{ emptyText: <Empty description="No datasets yet" /> }}
          dataSource={datasets}
          renderItem={(item) => (
            <List.Item
              actions={[]}
            >
              <List.Item.Meta
                title={<span>{item.name || `Dataset #${item.id}`} <Text type="secondary">{item.original_filename ? `(${item.original_filename})` : ''}</Text></span>}
                description={<Text type="secondary">Rows: {item.row_count ?? 0} • {item.created_at ? new Date(item.created_at).toLocaleString() : ''}</Text>}
              />
            </List.Item>
          )}
        />
      </Card>

      <Card title="1. Target Locations" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Title level={5} style={{ margin: 0 }}>Select or add the locations you want to analyze around</Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>Add Custom Location</Paragraph>
          <Form form={addressForm} layout="vertical">
            <Form.Item label="Address" name="address">
              <Input placeholder="Enter any address you want to target" allowClear />
            </Form.Item>
            <Form.Item label="Duration" name="duration" initialValue="30d">
              <Select options={durationOptions} style={{ maxWidth: 240 }} />
            </Form.Item>
            <Divider plain>Or</Divider>
            <Form.Item label="Upload CSV of addresses" tooltip="One address per line or with a header column">
              <Upload.Dragger {...uploadAddrProps}>
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                <p className="ant-upload-hint">CSV or TXT</p>
              </Upload.Dragger>
            </Form.Item>
            <Divider plain>Or</Divider>
            <Form.Item label="Paste addresses (one per line)" name="pastedAddresses">
              <Input.TextArea rows={4} placeholder="123 Main St, City, ST 00000\n456 Elm Ave, City, ST 00000" />
            </Form.Item>
            <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} loading={filtering}>Filter</Button>
          </Form>
        </Space>
      </Card>

      <Card title="2. Search Radius" style={{ marginBottom: 16 }}>
        <Paragraph>Configure the search distance around your target locations.</Paragraph>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text strong>Selected Target Locations then Search Radius</Text>
          <div className="map-placeholder">
            <Text type="secondary">
              Visual representation of search coverage area — for demonstration purposes only. Map does not reflect actual locations, addresses, or real-world data.
            </Text>
          </div>
          <Text strong>Quick Select</Text>
          <Space wrap>
            {[0.5, 1, 2.5, 5, 10, 20].map(v => (
              <Button key={v} onClick={() => quickSetRadius(v)} type={radius === v ? 'primary' : 'default'}>{v} mi</Button>
            ))}
          </Space>
          <Row gutter={16} align="middle">
            <Col xs={24} md={16}>
              <Slider min={0.5} max={50} step={0.5} value={radius} onChange={setRadius} tooltip={{ formatter: (v) => `${v} mi` }} />
            </Col>
            <Col xs={24} md={8}>
              <Space>
                <Text>Custom Radius:</Text>
                <InputNumber min={0.5} max={50} step={0.5} value={radius} onChange={setRadius} />
                <Text>miles</Text>
              </Space>
            </Col>
          </Row>
        </Space>
      </Card>

      <Card title={`Results (${filteredResults.length}${searchQuery ? ` of ${results.length}` : ''})`} ref={resultsCardRef}>
        {filteredResults.length === 0 ? (
          <div style={{ padding: '16px 0' }}>
            <Empty
              description={searchQuery ? 'No records match your search' : 'Run a filter to see matched addresses'}
            >
              {searchQuery ? (
                <Button type="link" onClick={() => { setSearchQuery(''); setPage(1); }}>Clear search</Button>
              ) : null}
            </Empty>
          </div>
        ) : (
          <>
            <div className="results-toolbar">
              <Space wrap>
                <Button>Filter & Export</Button>
                <Input
                  placeholder="Search Profiles"
                  style={{ width: 220 }}
                  allowClear
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                />
                <Tag>Max Distance: {radius} miles</Tag>
              </Space>
              <Space wrap>
                <Button onClick={() => {
                  const start = (page - 1) * 20;
                  const end = start + 20;
                  setSelectedIds(filteredResults.slice(start, end).map(r => r.id));
                }}>Select Page</Button>
                <Button onClick={() => setSelectedIds([])}>Clear All</Button>
                <Button onClick={() => exportResults(filteredResults.filter(r => selectedIds.includes(r.id)))} disabled={!selectedIds.length}>Export Selected</Button>
                <Button type="primary" onClick={() => exportResults(filteredResults)} disabled={!filteredResults.length}>Export All</Button>
                <Text type="secondary">Selected: {selectedIds.length} • {filteredResults.length} of {results.length} shown</Text>
              </Space>
            </div>
            
            <List
              grid={{ gutter: 16, column: 3 }}
              dataSource={filteredResults}
              rowKey={(p) => p.id || `${p.name || ''}-${p.email || ''}-${p.address || ''}`}
              pagination={{ pageSize: 20, current: page, onChange: setPage, showSizeChanger: false, total: filteredResults.length }}
              renderItem={(p) => (
                <List.Item key={p.id || `${p.name || ''}-${p.email || ''}-${p.address || ''}`}>
                  <Card size="small" className="profile-card" bordered>
                    <div className="profile-card-top">
                      <Checkbox
                        checked={selectedIds.includes(p.id)}
                        onChange={(e) => {
                          setSelectedIds((prev) => {
                            const set = new Set(prev);
                            if (e.target.checked) set.add(p.id); else set.delete(p.id);
                            return Array.from(set);
                          });
                        }}
                      />
                      <Avatar className="profile-avatar">{p.initials}</Avatar>
                      <div className="profile-title">
                        <div className="profile-name">{p.name}</div>
                        <div className="profile-sub">Age: {p.age}</div>
                      </div>
                    </div>
                    <Divider className="profile-divider" />
                    <div className="profile-meta-grid">
                      <div className="meta-item"><DollarOutlined /> <span>{p.income}</span></div>
                      <div className="meta-item"><HeartOutlined /> <span>{p.marital}</span></div>
                      <div className="meta-item"><HomeOutlined /> <span>{p.home}</span></div>
                      <div className="meta-item">{p.gender === 'Female' ? <WomanOutlined /> : <ManOutlined />} <span>{p.gender}</span></div>
                      <div className="meta-item"><TeamOutlined /> <span>Children: {p.children}</span></div>
                    </div>
                    <Divider className="profile-divider" />
                    <div className="profile-meta-grid">
                      <div className="meta-item full"><MailOutlined /> <span>{p.email}</span></div>
                      <div className="meta-item full"><PhoneOutlined /> <span>{p.phone}</span></div>
                      <div className="meta-item full"><EnvironmentOutlined /> <span>{p.address}</span></div>
                    </div>
                    <Divider className="profile-divider" />
                    <div className="profile-footer">
                      <span className="distance-link">{p.distance} away</span>
                      <div className="nearest"><Text type="secondary">Nearest target:</Text> {p.nearest}</div>
                    </div>
                  </Card>
                </List.Item>
              )}
            />
          </>
        )}
      </Card>
    </div>
  );
}

export default NeighborhoodLens;
